import H from '@here/maps-api-for-javascript'
import { useEffect, useRef } from 'react'

/**
 * useMarker is a low-level hook that puts a marker on a specific position on the map.
 * @param map The HERE map instance.
 * @param position The position of the marker.
 * @param placement The placement of the marker. Defaults to 'center'.
 * @param zIndex The z-index of the marker.
 * @returns A ref to the marker element. This ref must be passed to the actual marker element.
 */
export const useMarker = <T extends HTMLElement>({
  map,
  position,
  placement = 'center',
  zIndex,
}: {
  map?: H.Map
  position?: H.geo.IPoint
  placement?: 'center' | 'bottom-left'
  zIndex?: number
}) => {
  const markerRef = useRef<T>(null)

  useEffect(() => {
    if (map && markerRef.current && position) {
      markerRef.current.style.position = 'absolute'
      if (placement === 'center') {
        markerRef.current.style.left = `${-markerRef.current.offsetWidth / 2}px`
        markerRef.current.style.top = `${-markerRef.current.offsetHeight / 2}px`
      } else if (placement === 'bottom-left') {
        markerRef.current.style.top = `${-markerRef.current.offsetHeight}px`
      }
      const icon = new H.map.DomIcon(markerRef.current)
      const markerObject = new H.map.DomMarker(position, {
        icon,
        zIndex,
        data: {},
      })
      map.addObject(markerObject)

      return () => {
        map.removeObject(markerObject)
      }
    }
    return
  }, [map, position, placement, zIndex])

  return markerRef
}
